import store from "../../repository/store/store";
import { Menu } from "../../../domain/entities/courseEvent/training_course";
import { useStore } from "vuex";
import { wrapLoading } from "../utils/wrap_loading";
import { dispatchError } from "../utils/error_handling";

export const shuffle = <T>(array: T[]): T[] => {
  let currentIndex = array.length,
    randomIndex: number;

  // While there remain elements to shuffle.
  while (currentIndex != 0) {
    // Pick a remaining element.
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex]
    ];
  }

  return array;
};

export const dispatchErrorBox = async (e: string) => {
  try {
    await store.dispatch("miscellany_module/setError", {
      message: e,
      changed: true
    });
  } catch (e) {
    console.log(e);
  }
};

export const dispatchSuccessBox = async (e: string) => {
  try {
    await store.dispatch("miscellany_module/setSuccess", {
      message: e,
      changed: true
    });
  } catch (e) {
    console.log(e);
  }
};

/** Check if you have reached the daily decoin limit. */
export async function checkDailyDecoinLimit(): Promise<void> {
  try {
    await wrapLoading(async () => {
      await store.dispatch("decoins_module/dailyDecoinLimit");
      const dailyDecoinLimitExceed =
        store.getters["decoins_module/dailyDecoinLimit"];
      if (dailyDecoinLimitExceed) {
        await store.dispatch("decoins_module/setDecoinsLimitModal", true);
      }
    });
  } catch (e) {
    await dispatchError(e);
  }
}

let previousGenerated = "";

export function mask(value, mask) {
  /* Will receive the input + mask */
  let generated = "";

  /* used to calibrate the counting across the mask */
  let offset = 0;
  /* if the input is not a number, cancel */
  if (!value.match("[0-9]")) {
    return;
  }

  /* iterates across each currentChar's position and mask */
  for (let i = 0; i < mask.length && i < value.length; i++) {
    /* defines variables to be compared */
    const expectedChar = mask[offset + i];
    const currentChar = value[i];

    if (!expectedChar) {
      return previousGenerated;
    }

    /* verifies if the currentChar match with the expected char and apply the logist */
    if (expectedChar == "#") {
      if (currentChar.match("[0-9]")) {
        generated += currentChar;
      }
    } else if (currentChar == expectedChar) {
      generated += currentChar;
    } else {
      offset++;
      generated += expectedChar;
      generated += currentChar;
    }
  }

  /* users will see the input with the mask */
  value = generated;
  previousGenerated = generated;
  return value;
}

/** Return a copy of the giving object by value. */
export const getObjectByValue = (object: any) =>
  JSON.parse(JSON.stringify(object));

/**
 * Returns a date formatted as DD/MM/YY
 * @param date todo: explain this
 */
export function formattedDate(date: string) {
  const divisor = date.indexOf(" ");
  let formattedDate = date.substring(0, divisor).replaceAll("-", " / ");
  const firstPoint = formattedDate.indexOf("/", 1);
  const lastPoint = firstPoint + 3;
  const year = formattedDate.substr(0, firstPoint).trim();
  const mouth = formattedDate.substr(firstPoint + 1, 3).trim();
  const day = formattedDate.substr(lastPoint + 3, 3).trim();
  formattedDate = `${day}/${mouth}/${year}`;

  return formattedDate;
}

export const formatDateOnly = (date: string): string => {
  const dates = date.split("-");
  return `${dates[2]}/${dates[1]}/${dates[0]}`;
};

/** scroll to the top of view. */
export const scrollTo = (x: number, y: number) => {
  const element = document.querySelector("[data-view]");

  if (element) {
    element.scrollTo(x, y);
  }
};

/** return a cloned object but not by referent. */
export const cloneObject = (object: unknown): unknown => {
  return JSON.parse(JSON.stringify(object));
};

/** Returns a string format from any value. */
export const stringify = (value: any): string => {
  return typeof value !== "string" ? JSON.stringify(value) : value;
};

/** Scrolls smooth to the top of the view */
export const scrollToTop = (): void => {
  const element = document.querySelector(
    "[data-top-element]"
  ) as HTMLSpanElement;
  if (element) {
    element.scrollIntoView({ block: "start" });
  }
};

/** Scrolls smooth to the element identified by data attribute */
export const scrollSmoothToElement = (data: string): void => {
  const element = document.querySelector(`${data}`) as HTMLSpanElement;
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

/**
 * @param duration is a number in milliseconds.
 * @returns the following formats: 00:00:00h, 00:00m in case the hours are less than zero and 00h for too many hours.
 */
export function formattedDuration(duration: number) {
  const sec = duration;
  let hours: string | number = Math.floor(sec / 3600);
  let minutes: string | number = Math.floor((sec - hours * 3600) / 60);
  let seconds: string | number = sec - hours * 3600 - minutes * 60;

  if (hours < 10) {
    hours = "0" + hours;
  }
  if (minutes < 10) {
    minutes = "0" + minutes;
  }
  if (seconds < 10) {
    seconds = "0" + seconds;
  }

  if (hours > 24 || (minutes == 0 && seconds == 0)) {
    return hours + "h";
  }
  if (hours == 0 && minutes == 0) {
    return seconds + "s";
  }
  if (hours == 0 && seconds == 0) {
    return minutes + "m";
  }
  if (hours == 0) {
    return minutes + "m" + seconds + "s";
  }
  return hours + "h" + minutes + "m";
}

/** Format the date to: dd/mm/yyyy*/
export function getLocalFormatDate(date: string): string {
  if (!date) return "";
  const arrayDateEndHour = date.split(" ");
  return arrayDateEndHour[0]
    .split("-")
    .reverse()
    .join("/");
}

export function getLocalFormatHour(date: string): string {
  if (!date) return "";
  const arrayDateEndHour = date.split(" ");
  return arrayDateEndHour[1];
}

/** Function responsible for removing all spaces from a string and joining with +.
 * Function used in search bar to format string for the request. */
export function formatSearchQuery(text: string): string {
  const arrayStrings = text.split(" ");

  return arrayStrings.filter(value => value !== "").join("+");
}

/** Function responsible for displaying the first decimal place of the number. */
export function toFixed(num, fixed): string {
  const regex = new RegExp("^-?\\d+(?:.\\d{0," + (fixed || -1) + "})?");
  return num.toString().match(regex)[0];
}

/** Return the date formatted to locale date*/
export const toLocaleDateTime = (date: string) => {
  const options = {
    month: "numeric",
    year: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric"
  };
  return new Date(date).toLocaleString("pt-br", options) + "h";
};

/** Function responsible for formatting the time to 00h00m. */
export function formatHour(hour: string): string {
  if (!hour) return "";
  const arrayHour = hour.split(":");
  const hourUpdate = arrayHour[0] + "h";
  return hourUpdate + arrayHour[1];
}

/** Function responsible for formatting the date to 00/00/0000 às 00h00m. */
export function formatDateTime(date: string): string {
  if (!date) return "";

  const divisor = date.indexOf(" ");
  let formatDate = date.substring(0, divisor).replaceAll("-", " / ");

  const firstPoint = formatDate.indexOf("/", 1);
  const lastPoint = firstPoint + 3;

  const year = formatDate.substr(0, firstPoint).trim();
  let month = formatDate.substr(firstPoint + 1, 3).trim();
  const day = formatDate.substr(lastPoint + 3, 3).trim();
  const hour = formatHour(date.substring(divisor + 1, date.length));

  if (month.includes("/")) {
    month = month.replace("/", "");
  }

  formatDate = `${day}/${month}/${year} às ${hour}`;
  return formatDate;
}

/** Converts a value to percentage based on a given total value.
 * @param numerator The value that's going to be converted to percentage.
 * @param denominator The base value used in the formula.
 * @param rounded Tells whether the return value will be rounded (using Math.floor) or not. */
export function toPercentage(
  numerator: number,
  denominator: number,
  rounded?: boolean
): number {
  if (numerator == 0 || denominator == 0) return 0;
  return rounded
    ? Math.floor((numerator / denominator) * 100)
    : (numerator / denominator) * 100;
}

export const maskCPF = (input: string): string => {
  let cpf = "";

  /** Remove everything that is not a number. */
  cpf = input.replace(/\D+/g, "");
  /** Place a dot between the third and fourth numbers. */
  cpf = cpf.replace(/(\d{3})(\d)/, "$1.$2");
  /** Back to the second block of numbers */
  cpf = cpf.replace(/(\d{3})(\d)/, "$1.$2");
  /** Put a hyphen between the third and fourth numbers. */
  cpf = cpf.replace(/(\d{3})(\d{1,2})$/, "$1-$2");

  return cpf;
};

export const maskCEP = (input: string): string => {
  let cep = "";

  cep = input.replace(/\D+/g, "");
  cep = cep.replace(/(\d{5})(\d)/, "$1-$2");
  cep = cep.replace(/(-\d{3})\d+?$/, "$1");

  return cep;
};

export const maskCNPJ = (input: string): string => {
  let cnpj = "";

  /** Remove everything that is not a number. */
  cnpj = input.replace(/\D+/g, "");

  /** Place dot between second and third number. */
  cnpj = cnpj.replace(/^(\d{2})(\d)/, "$1.$2");

  /** Place dot between fifth and sixth number. */
  cnpj = cnpj.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");

  /** Put a slash between the eighth and ninth number. */
  cnpj = cnpj.replace(/\.(\d{3})(\d)/, ".$1/$2");

  /** Put a hyphen after the block of four numbers. */
  cnpj = cnpj.replace(/(\d{4})(\d)/, "$1-$2");

  return cnpj;
};

export  function formatBirthDate(birthDate: string) {
  if (!birthDate) return;
  let formatedDate = birthDate.replaceAll("-", "/");
  const firstPoint = formatedDate.indexOf("/", 1);
  const lastPoint = firstPoint + 1;
  const year = formatedDate.substr(0, firstPoint).trim();
  const mouth = formatedDate.substr(firstPoint + 1, 3).trim();
  const day = formatedDate.substr(lastPoint + 3, 3).trim();
  formatedDate = `${day}/${mouth}${year}`;
  return formatedDate;
}

const acceptedExtention = {
  png: true,
  jpg: true,
  jpeg: true,
  webp: true,
  PNG: true,
  JPG: true,
  JPEG: true,
  WEBP: true
};

export function handleImageExtention(filename: string) {
  const extention =
    filename
      .split(".")
      .pop()
      ?.toLowerCase() || "";
  if (!acceptedExtention[extention]) {
    dispatchErrorBox("Por favor, insira uma imagem PNG, JPG, JPEG ou WEBP");
    return "";
  } else {
    return "." + extention;
  }
}

/** Disable the body overflow to not let the user scroll the page behind some modal while is open. */
export const disableBodyOverflow = () => {
  document.body.style.overflow = "hidden";
};

/** Enable the body overflow to let the user scroll the page after close some modal. */
export const enableBodyOverflow = () => {
  document.body.style.overflow = "auto";
};

export const isCirculoTemplate = (): boolean => {
  return process.env.VUE_APP_THEME === "circulo";
};

export const isDecolaTemplate = (): boolean => {
  return process.env.VUE_APP_THEME === "default";
};

export const isConstrucolorTemplate = (): boolean => {
  return process.env.VUE_APP_THEME === "construcolor";
};

export const isGTopTemplate = (): boolean => {
  return process.env.VUE_APP_THEME === "gtop";
};

/** Function responsible for formatting the decoins according to the value.
 * Formatting is done when the screen is smaller than 700px.
 * @param decoins decoins to format. */
export function formatUserDecoins(decoins: number): string | number {
  if (window.matchMedia("(max-width:700px)").matches) {
    if (String(decoins).length) {
      if (String(decoins).length <= 3) {
        return decoins;
      } else if (String(decoins).length <= 6 && String(decoins).length > 3) {
        return toFixed(decoins / 1000, 1) + "k";
      } else if (String(decoins).length >= 7)
        return toFixed(decoins / 1000000, 1) + "m";
    } else {
      return "";
    }
  }
  return Intl.NumberFormat("pt-br").format(decoins);
}

/**
 * Formats a list of `Menu` objects into a string representation of their dates, grouped by year and month.
 * This function organizes the dates into readable string, grouping days by month and year. Days with the same month are listed numerically and separated by ',',
 * with the final day preceded by "e".
 * @param {Menu[]} menus - An array of `Menu` objects, each representing an event with a `startDate`.
 * @returns {string} A formatted string representing the grouped dates of the provided `Menu` objects.
 */
export function formatMenusDates(menus: Menu[]): string {
  const formattedDates: {
    [year: string]: {
      [month: string]: Menu[];
    };
  } = {};

  for (const menu of menus) {
    const [year, month] = menu.startDate.split("-");

    if (!formattedDates[year]) formattedDates[year] = {};

    if (!formattedDates[year][month]) formattedDates[year][month] = [];

    formattedDates[year][month].push(menu);
  }

  if (!Object.keys(formattedDates).length) return "";

  let formattedString = "";
  const years = Object.keys(formattedDates);
  const lastYear = years[years.length - 1];

  years.forEach(year => {
    let months = Object.keys(formattedDates[year]);
    months = months.sort((one, two) => Number(one) - Number(two)); //order by date
    const lastMonth = months[months.length - 1];

    months.forEach(month => {
      const trainingDataList = formattedDates[year][month];
      const lastTrainingData = trainingDataList[trainingDataList.length - 1];

      trainingDataList.forEach((trainingData, _) => {
        const day = trainingData.startDate.split("-")[2];

        if (formattedString === "") {
          formattedString = day;
        } else {
          if (
            trainingData === lastTrainingData &&
            trainingDataList.length > 1
          ) {
            formattedString += " e " + day;
          } else {
            formattedString += ", " + day;
          }
        }

        const isLastTrainingData = trainingData === lastTrainingData;
        const isLastMonth = month === lastMonth;
        const isLastYear = year === lastYear;

        if (isLastTrainingData) {
          formattedString += `/${month}`;
          if (isLastMonth) {
            formattedString += `/${year}`;
            if (!isLastYear) {
              formattedString += ", ";
            }
          } else {
            formattedString += ",";
          }
          formattedString = formattedString.replace(", ,", ",");
          formattedString = formattedString.replace(",,", ",");
        }
      });
    });
  });

  formattedString = subsCommaPerLetterE(formattedString);
  return formattedString;
}

function subsCommaPerLetterE(str: string): string {
  const numVirgulas = (str.match(/,/g) || []).length;

  if (numVirgulas > 1) {
    const lastCommaIndex = str.lastIndexOf(",");
    return (
      str.substring(0, lastCommaIndex) +
      " e" +
      str.substring(lastCommaIndex + 1)
    );
  }

  return str;
}

export function formatOneTime(date) {
  const newDate = new Date(date);
  let hours = newDate
    .getHours()
    .toString()
    .padStart(2, "0");
  let minutes = newDate
    .getMinutes()
    .toString()
    .padStart(2, "0");
  return `${hours}:${minutes}`;
}

/**
 * This function organizes the events by year, month, and time range, then formats them into a string.
 * Each event is grouped by its start date and time range, with the dates being formatted and listed numerically.
 * The events are separated by '|', and each group of events includes the time range during which they occur.
 * If multiple events occur on different days but within the same month and year, they are listed together.
 * If an event spans multiple months or years, it is listed separately.
 * @param {Menu[]} menus - An array of `Menu` objects, each representing an event with a `startDate`, `startHour`, and `endHour`.
 * @returns {string} A formatted string representing the ordined schedules
 */
export function formatMenusSchedule(menus: Menu[]): string {
  let result = "";
  const groupedEvents = groupEventsByTimeRange(menus);

  //when all dates only have one time range
  if (Object.keys(groupedEvents).length === 1) {
    return Object.keys(groupedEvents)[0];
  }

  for (const timeRange in groupedEvents) {
    let dates: string[] = [];
    for (const year in groupedEvents[timeRange]) {
      const months = Object.keys(groupedEvents[timeRange][year]);
      const lastMonth = ("0" + Math.max(...months.map(Number)))
        .slice(-2)
        .toString();
      for (const month in groupedEvents[timeRange][year]) {
        const days = groupedEvents[timeRange][year][month].map(
          date => date.split("/")[0]
        );
        const yearMonth = month === lastMonth ? `${month}/${year}` : month;
        dates.push(`${days.join(", ")}/${yearMonth}`);
      }
    }
    result += `${dates.join(", ")} das ${timeRange}, `;
  }
  result = result.slice(0, -2).trim();

  if (!result.includes(",")) {
    const parts = result.split("das ");
    result = parts[1];
  }

  return result;
}

/**
 * Groups events by year, month, and time range.
 * @returns An object with a nested structure where the first level of keys are years, the second level are months within those years,
 *          and the third level are time ranges within those months. Each time range key maps to an array of strings,
 *          each representing a day within that time range, formatted as "DD/MM/YYYY".
 */
function groupEventsByTimeRange(
  events: Menu[]
): {
  [timeRange: string]: {
    [year: string]: {
      [month: string]: string[];
    };
  };
} {
  return events.reduce((accumulator, event) => {
    const [year, month, day] = event.startDate.split("-");
    const timeRange = `${event.startHour} às ${event.endHour}`;

    if (!accumulator[timeRange]) accumulator[timeRange] = {};
    if (!accumulator[timeRange][year]) accumulator[timeRange][year] = {};
    if (!accumulator[timeRange][year][month])
      accumulator[timeRange][year][month] = [];

    accumulator[timeRange][year][month].push(`${day}/${month}/${year}`);

    return accumulator;
  }, {});
}

/**
 * Generates a string listing all unique locations from a given array of `Menu` objects.
 * @param menus An array of `Menu` objects, each representing an event with a `location`.
 * @returns A string representing the locations of the events.
 */
export function getMenusLocation(menus: Menu[]): string {
  let location = "";
  let menusLocationArray: string[] = [];

  menus.forEach(menu => {
    if (!menusLocationArray.includes(menu.location))
      menusLocationArray.push(menu.location);
  });

  menusLocationArray.forEach((value, index) => {
    if (index == 0) {
      location = value;
    } else if (index == menusLocationArray.length - 1) {
      location += " e " + value;
    } else {
      location += ", " + value;
    }
  });

  return location;
}

/** Format the date to: yyyy-mm-ddd */
export function getDate(date: string): string {
  if (!date) return "";
  const arrayDateEndHour = date.split(" ");
  return arrayDateEndHour[0];
}

/** Grouped the date to: 20220712 */
export function dateGrouped(date: string): string {
  if (!date) return "";
  return date.split("-").join("");
}

export function hourGrouped(time: string): string {
  if (!time) return "";
  return time.split(":").join("");
}
